/**
 * useMobileAnalyticsData Hook
 * 
 * Handles all data processing logic for mobile analytics:
 * - Transaction filtering
 * - Category/group aggregation
 * - Total amount calculation
 */

import { useMemo, useRef, useEffect } from "react";
import { TransactionWithDate } from "../../Analytics/hooks/useAnalyticsTransactions";
import { Account, TransactionCategoryGroup } from "../../../types";
import { CategoryData } from "../../Analytics/components/AnalyticsPieChart";
import { getCategoryColor } from "../../Analytics/hooks/useAnalyticsHelpers";

export interface UseMobileAnalyticsDataParams {
  transactions: TransactionWithDate[];
  categories: Array<{ name: string; groupId?: string }>;
  groups: TransactionCategoryGroup[];
  accounts: Account[];
  fromDate: string;
  toDate: string;
  selectedAccounts: string[];
  selectedUsers: string[];
  selectedTab: "expenses" | "incomes" | "adjustments";
  aggregationMode: "category" | "group";
  categoriesLoading: boolean;
  defaultCurrencyLoaded: boolean;
  defaultCurrency: string;
  currencies: Array<{ code: string }>;
  convertToDefaultCurrency: (
    amount: number, 
    currency: string | undefined,
    amountInDefaultCurrency?: number,
    defaultCurrencyAtTime?: string
  ) => number;
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
}

export interface UseMobileAnalyticsDataReturn {
  filteredTransactions: TransactionWithDate[];
  categoryData: CategoryData[];
  totalAmount: number;
}

export function useMobileAnalyticsData({
  transactions,
  categories,
  groups,
  accounts,
  fromDate,
  toDate,
  selectedAccounts,
  selectedUsers,
  selectedTab,
  aggregationMode,
  categoriesLoading,
  defaultCurrencyLoaded,
  defaultCurrency,
  currencies,
  convertToDefaultCurrency,
  getAccountName,
}: UseMobileAnalyticsDataParams): UseMobileAnalyticsDataReturn {
  
  // Track previous defaultCurrency to detect changes
  const previousDefaultCurrencyRef = useRef<string>(defaultCurrency);
  const currencyChangeTimestampRef = useRef<number>(0);
  
  // Detect when defaultCurrency changes
  useEffect(() => {
    if (previousDefaultCurrencyRef.current !== defaultCurrency) {
      console.log('[useMobileAnalyticsData] Default currency changed', {
        from: previousDefaultCurrencyRef.current,
        to: defaultCurrency,
        timestamp: new Date().toISOString()
      });
      previousDefaultCurrencyRef.current = defaultCurrency;
      // Mark when currency changed - currencies will be refetched
      currencyChangeTimestampRef.current = Date.now();
    }
  }, [defaultCurrency]);
  
  // Create Sets for efficient filtering
  const selectedUsersSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);
  const selectedAccountsSet = useMemo(() => new Set(selectedAccounts), [selectedAccounts]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Filter by selected tab type
      if (selectedTab === "expenses" && transaction.type !== "expense") return false;
      if (selectedTab === "incomes" && transaction.type !== "income") return false;
      if (selectedTab === "adjustments" && transaction.type !== "adjustment") return false;

      // Date range filter - use string comparison to avoid timezone issues
      if (fromDate || toDate) {
        const transactionDate = transaction.date;
        if (!transactionDate) return false;
        
        const transactionDateStr = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}-${String(transactionDate.getDate()).padStart(2, '0')}`;
        
        if (fromDate && transactionDateStr < fromDate) return false;
        if (toDate && transactionDateStr > toDate) return false;
      }

      // Account filter
      if (selectedAccounts.length > 0 && transaction.accountId) {
        if (!selectedAccountsSet.has(transaction.accountId)) return false;
      }

      // User filter
      if (selectedUsers.length > 0 && transaction.userId) {
        if (!selectedUsersSet.has(transaction.userId)) return false;
      }

      return true;
    });
  }, [transactions, fromDate, toDate, selectedAccounts, selectedAccountsSet, selectedTab, selectedUsers, selectedUsersSet]);

  // Create mapping from category name to group name
  // Only build map when categories and groups are loaded (not while loading)
  const categoryToGroupMap = useMemo(() => {
    // Don't build map if still loading or if no data - return empty map
    // Check categoriesLoading first, then check if we have data
    if (categoriesLoading) {
      return new Map<string, string>();
    }
    
    // If no categories or groups, return empty map
    if (categories.length === 0 || groups.length === 0) {
      return new Map<string, string>();
    }

    const map = new Map<string, string>();
    categories.forEach((cat) => {
      if (cat.groupId) {
        const group = groups.find((g) => g.id === cat.groupId);
        if (group) {
          map.set(cat.name, group.name);
        }
      }
    });
    return map;
  }, [categories, groups, categoriesLoading]);

  // Group transactions by category (expenses/incomes) or account (adjustments)
  const categoryData = useMemo((): CategoryData[] => {
    // Wait for default currency and currencies to be loaded before calculating
    // This prevents showing intermediate values with wrong currency conversions
    const hasDefaultCurrencyInList = currencies.some(c => c.code === defaultCurrency);
    if (!defaultCurrencyLoaded || currencies.length === 0) {
      return [];
    }

    // Additional check: if we have currencies but default currency is not in the list,
    // it might mean currencies are still loading for the new default currency
    const currencyRecentlyChanged = Date.now() - currencyChangeTimestampRef.current < 500;
    if (!hasDefaultCurrencyInList && (currencies.length > 0 || currencyRecentlyChanged)) {
      return [];
    }

    // If switching to group mode, ensure categories and groups are fully loaded before calculating
    if (aggregationMode === "group" && selectedTab !== "adjustments") {
      if (categoriesLoading || categories.length === 0 || groups.length === 0 || categoryToGroupMap.size === 0) {
        return [];
      }
    }
    
    const categoryMap = new Map<string, number>();

    filteredTransactions.forEach((transaction) => {
      let category: string;
      
      if (transaction.type === "adjustment") {
        category = getAccountName(transaction.accountId, transaction.accountName) || "Unknown Account";
      } else {
        const transactionCategory = transaction.category || "Other";
        
        if (aggregationMode === "group" && selectedTab !== "adjustments") {
          // Map category name to group name
          // At this point, we know data is ready (checked above)
          const groupName = categoryToGroupMap.get(transactionCategory);
          // Always use category name if no group found, never use "Uncategorized"
          // This is expected behavior - categories without groups show as category names
          category = groupName || transactionCategory;
        } else {
          category = transactionCategory;
        }
      }
      
      const originalAmount = transaction.amount || 0;
      // Prefer using amountInDefaultCurrency if available (stored at transaction time)
      const convertedAmount = convertToDefaultCurrency(
        originalAmount, 
        transaction.currency,
        transaction.amountInDefaultCurrency,
        transaction.defaultCurrencyAtTime
      );
      categoryMap.set(category, (categoryMap.get(category) || 0) + convertedAmount);
    });
    
    const sortedData = Array.from(categoryMap.entries())
      .map(([name, value]) => ({
        name,
        value: Math.round(value * 100) / 100,
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    return sortedData.map((item, index) => ({
      name: item.name,
      value: item.value,
      color: getCategoryColor(index),
    }));
  }, [filteredTransactions, aggregationMode, categoryToGroupMap, selectedTab, categories.length, groups.length, defaultCurrencyLoaded, defaultCurrency, currencies, convertToDefaultCurrency, getAccountName]);

  // Calculate total amount
  const totalAmount = useMemo(() => {
    const hasDefaultCurrencyInList = currencies.some(c => c.code === defaultCurrency);
    if (!defaultCurrencyLoaded || currencies.length === 0) {
      return 0;
    }
    const currencyRecentlyChanged = Date.now() - currencyChangeTimestampRef.current < 500;
    if (!hasDefaultCurrencyInList && (currencies.length > 0 || currencyRecentlyChanged)) {
      return 0;
    }
    
    return filteredTransactions.reduce((sum, t) => {
      const originalAmount = t.amount || 0;
      // Prefer using amountInDefaultCurrency if available (stored at transaction time)
      const convertedAmount = convertToDefaultCurrency(
        originalAmount, 
        t.currency,
        t.amountInDefaultCurrency,
        t.defaultCurrencyAtTime
      );
      return sum + convertedAmount;
    }, 0);
  }, [filteredTransactions, defaultCurrencyLoaded, defaultCurrency, currencies, convertToDefaultCurrency]);

  return {
    filteredTransactions,
    categoryData,
    totalAmount,
  };
}
